<template>
  <slot></slot>
  <modal :id="modalId" :title="modalTitle">

    <!-- Begin of "MAIN" mode. -->
    <div v-if="mode === 'main'">

      <!-- Text campaign type field -->
      <div class="form-group row my-3">
        <label for="type" class="col-form-label col-sm-2">Type:</label>
        <div class="col-sm-10">
          <div class="btn-group d-flex" role="group">
            <a @click="setType(typeId)"
               v-for="(typeName, typeId) in types"
               :class="'btn btn-default' + (typeId === type ? ' btn-secondary active' : '')">
              {{ typeName }}
            </a>
          </div>
        </div>
      </div>

      <!-- List of storms for storm deployment text campaigns -->
      <div v-if="isDeployment" class="form-group row my-3">
        <label for="storm_id" class="col-form-label col-sm-2">Storm:</label>
        <div class="col-sm-10">
          <select v-model="stormId" class="form-control form-control-sm">
            <option v-for="storm in storms" :value="storm.id">{{ storm.name }}</option>
          </select>
        </div>
      </div>

      <!-- Text campaign "create new" or "load existing" selector -->
      <div v-if="isCampaign && campaignIdOptions.length" class="form-group row my-3">
        <label for="id" class="col-form-label col-sm-2">Campaign:</label>
        <div class="col-sm-10">
          <select v-model="id" class="form-control form-control-sm">
            <option v-for="campaign in campaignIdOptionsWithNew" :value="campaign.id">{{ campaign.title }}</option>
          </select>
        </div>
      </div>

      <!-- Text campaign title field -->
      <div v-if="isCampaign && !existingTextCampaign" class="form-group row my-3">
        <label for="title" class="col-form-label col-sm-2">Title:</label>
        <div class="col-sm-10">
          <input v-model="title" type="text" class="form-control form-control-sm" placeholder="Enter a text campaign title">
          <div v-if="!title" class="text-danger">Title is required.</div>
        </div>
      </div>

      <!-- Text campaign text field -->
      <div v-if="existingTextCampaign" class="form-group row my-3">
        <label for="text" class="col-form-label col-sm-2">Text:</label>
        <div class="col-sm-10">
          <div class="alert alert-secondary">{{ existingTextCampaign.text }}</div>
        </div>
      </div>
      <div v-else class="form-group row my-3">
        <label for="text" class="col-form-label col-sm-2">Text:</label>
        <div class="col-sm-10">
          <textarea v-model="text" class="form-control form-control-sm" cols="50" rows="10" placeholder="Enter a text to send out to adjusters"></textarea>
          <div v-if="!text" class="text-danger">Text is required.</div>
        </div>
      </div>

      <!-- Automatic response settings -->
      <div v-if="!existingTextCampaign">
        <text-campaign-response-editor
            field-name="reply_options"
            @valueChanged="responseOptionsChanged"
            :create-campaign-mode="true"
            :initial-is-deployment="isDeployment"
            :response-list="arsResponseList"
            :initial-existing-settings="arsInitialExistingSettings"
            :default-deployment-items="arsDefaultDeploymentItems"
            :default-campaign-items="arsDefaultCampaignItems"
            :statuses="adjusterStatuses"
            :status-label-data="adjusterStatusLabelData"
        >
        </text-campaign-response-editor>
      </div>

      <!-- Sent deployment campaign warning -->
      <div v-if="showSentDeploymentWarning" class="alert alert-danger mb-0 text-center">
        The selected storm deployment campaign is already sent and not available for updating.
      </div>

      <!-- Archived deployment campaign warning -->
      <div v-if="showArchivedDeploymentWarning" class="alert alert-danger mb-0 text-center">
        The selected storm deployment campaign is archived and not available for updating.
      </div>

    </div>
    <!-- End of "MAIN" mode. -->

    <!-- Begin of "CONFIRM" mode. -->
    <div v-if="mode === 'confirm'">

      <div class="btn-group d-flex" role="group">
        <a @click="submit = 'send'" :class="'btn btn-default' + (submit === 'send' ? ' btn-secondary active' : '')">
          <i class="fa fa-fw fa-send"></i> Send Text Campaign
        </a>
        <a @click="submit = 'save'" :class="'btn btn-default' + (submit === 'save' ? ' btn-secondary active' : '')">
          <i class="fa fa-fw fa-save"></i> Save Text Campaign
        </a>
      </div>

      <div class="alert alert-warning text-center mt-2">
            <span v-if="submit === 'send'">
              A {{ isDeployment ? 'storm deployment ' : ''}}text campaign will be {{ existingTextCampaign ? 'updated' : 'created' }} and sent immediately.<br>
              Are you sure you want to contact <strong>{{ adjusterIds.length }}</strong> adjusters within the {{ isDeployment ? 'storm deployment ' : ''}}text campaign?
            </span>

        <span v-if="submit === 'save'">
              The {{ isDeployment ? 'storm deployment ' : ''}}text campaign will be {{ existingTextCampaign ? 'updated' : 'created' }} but not sent. You can make changes and send it later.<br>
              Are you sure you want to add <strong>{{ adjusterIds.length }}</strong> adjusters to the {{ isDeployment ? 'storm deployment ' : ''}}text campaign?
            </span>
      </div>

      <div v-if="error" class="alert alert-danger text-center mt-2">
        {{ error }}
      </div>

    </div>
    <!-- End of "CONFIRM" mode. -->

    <!-- Begin of "SUCCESS" mode. -->
    <div v-if="mode === 'success'">
      <div class="alert alert-success text-center mt-2">
        <h3>
          <i class="fa fa-check-circle fa-spin"></i>
          The operation is successful. Redirecting to campaign page...
        </h3>
      </div>
    </div>
    <!-- End of "SUCCESS" mode. -->

    <template v-slot:footer>
      <button type="button" class="btn btn-default" @click="hideModal"><i class="fa fa-fw fa-ban"></i> Cancel</button>

      <template v-if="mode === 'main'">
        <button type="button" class="btn btn-secondary" :disabled="!proceedButtonAvailable" @click="mode = 'confirm'">
          <i class="fa fa-fw fa-arrow-right"></i> Proceed
        </button>
      </template>

      <template v-if="mode === 'confirm'">
        <button type="button" class="btn btn-secondary" @click="mode = 'main'"><i class="fa fa-fw fa-arrow-left"></i> Back</button>
        <button type="button" class="btn btn-success" @click="submitData()" :disabled="processingSubmit">
          <i :class="'fa fa-fw fa-' + (submit === 'save' ? 'save' : 'send')"></i> Confirm {{ submit }}
          <i class="fa fa-spinner fa-spin" v-if="processingSubmit"></i>
        </button>
      </template>

      <template v-if="mode === 'success'">
        <button type="button" class="btn btn-default" @click="hideModal"><i class="fa fa-fw fa-ban"></i> Close</button>
      </template>
    </template>

  </modal>
</template>

<script>
  import TextCampaignResponseEditor from "./TextCampaignResponseEditor";
  import HasModal from "../mixins/HasModal";
  import { mapState } from 'pinia'
  import { useCheckboxListStore } from "../../stores/checkboxList"

  export default {
    mixins: [HasModal],

    components: {
      TextCampaignResponseEditor
    },

    props: {

      action: {
        type: String,
        required: true,
      },

      modalId: {
        type: String,
        default: 'textCampaignModal'
      },

      types: {
        type: Object,
        default: () => {return {};}
      },

      deploymentType: {
        type: String,
        required: true,
      },

      defaultType: {
        type: String,
        required: true,
      },

      defaultTitle: {
        type: String,
        default: '',
      },

      existingCampaigns: {
        type: Object,
        required: true,
      },

      storms: {
        type: Array,
        required: true,
      },

      // For automatic response settings (ARS).
      arsResponseList: {
        type: Object,
        default: () => {return {};}
      },

      arsDefaultDeploymentItems: {
        type: Array,
        default: () => {return [];}
      },

      arsDefaultCampaignItems: {
        type: Array,
        default: () => {return [];}
      },

      arsInitialExistingSettings: {
        type: Array,
        default: () => {return [];}
      },

      adjusterStatuses: {
        type: Object,
        default: () => {return {};}
      },

      adjusterStatusLabelData: {
        type: Object,
        default: () => {return {};}
      },

    },

    /**
     * Variables (models).
     */
    data () {
      return {

        modalTitle: 'Text Campaign',
        mode: 'main',
        processingSubmit: false,
        error: '',

        // Form fields.
        type: this.defaultType,
        title: this.defaultTitle,
        text: '',
        id: 0,
        stormId: this.storms[0].id,
        replyOptions: '',
        submit: 'save',

      }
    },

    /**
     * Computed values
     */
    computed: {
      ...mapState(useCheckboxListStore, ['checkedIds']),

      adjusterIds() {
        return this.checkedIds();
      },

      isDeployment() {
        return this.type === this.deploymentType;
      },

      isCampaign() {
        return !this.isDeployment;
      },

      campaignIdOptions() {
        return this.existingCampaigns[this.type] ?? [];
      },

      campaignIdOptionsWithNew() {
        return [{"id": 0, "title":"New"}, ...this.campaignIdOptions];
      },

      existingDeployment() {
        return this.isDeployment ? this.existingCampaigns[this.deploymentType].find(campaign => parseInt(campaign.storm_id) === this.stormId) : null;
      },

      existingCampaign() {
        if (!this.type) return null;
        return this.isCampaign ? this.existingCampaigns[this.type].find(campaign => parseInt(campaign.id) === this.id) : null;
      },

      existingTextCampaign() {
        return this.isDeployment ? this.existingDeployment : this.existingCampaign;
      },

      showSentDeploymentWarning() {
        return this.isDeployment && this.existingTextCampaign && this.existingTextCampaign.is_started && this.existingTextCampaign.status === 1;
      },

      showArchivedDeploymentWarning() {
        return this.isDeployment && this.existingTextCampaign && (this.existingTextCampaign.status !== 1);
      },

      proceedButtonAvailable() {
        // Block archived and sent storm deployments.
        if (this.showSentDeploymentWarning || this.showArchivedDeploymentWarning) return false;
        if (this.existingTextCampaign) {
          // Allow all existing campaigns.
          return true;
        } else {
          // If campaign is new, block empty text.
          if (!this.text) return false;
          // And block empty title (except storm deployments).
          return !(!this.isDeployment && !this.title);
        }
      },

    },

    /**
     * Functions (methods).
     */
    methods: {

      setType(type) {
        this.type = type;
      },

      responseOptionsChanged(value) {
        this.replyOptions = value;
      },

      submitData() {
        let formData = this.formData();
        this.processingSubmit = true;
        axios.post(this.action, formData, formData)
          .then(response => {
            this.mode = 'success';
            window.location.href = response.data.redirect;
          })
          .catch(error =>{
            this.handleErrors(error, 'Unable to process request.')
          })
          .then(() =>{
            this.processingSubmit = false;
          });
      },

      formData() {
        const formData = new FormData();
        formData.append('_token', this.$csrf_token);
        this.adjusterIds.forEach(adjusterId => formData.append('adjuster_ids[]', adjusterId));
        formData.append('type', this.type);
        formData.append('title', this.isDeployment ? '' : (this.existingTextCampaign ? this.existingTextCampaign.title : this.title));
        formData.append('text', this.existingTextCampaign ? this.existingTextCampaign.text : this.text);
        formData.append('id', this.isDeployment ? 0 : this.id);
        formData.append('submit', this.submit);
        if (this.isDeployment) formData.append('storm_id', this.stormId);
        if (!this.existingTextCampaign) formData.append('reply_options', this.replyOptions);
        return formData;
      },

      handleErrors(errorObj, defaultMessage) {
        if (errorObj.response.status === 422) {
          let errors = errorObj.response.data.errors || {};
          let errorMessages = [];
          Object.keys(errors).forEach(field => {
            errorMessages.push(errors[field]);
          })
          this.error = errorMessages.join(' ');
        } else {
          this.error = defaultMessage;
        }
      },

    },
  }
</script>
